import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FlashcardPronunciationAssessmentPhoneme,
  FlashcardPronunciationAssessmentResponse,
  FlashcardPronunciationAssessmentWord,
  FlashcardService,
} from '@core/services/flashcard.service';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { Subscription, finalize } from 'rxjs';
import { TrainFlashcard } from '../../../deck-train.models';

type PronunciationTextToken = {
  index: number;
  text: string;
  kind: 'word' | 'space';
  word: FlashcardPronunciationAssessmentWord | null;
};

@Component({
  selector: 'app-pronunciation-train-front',
  imports: [DecimalPipe],
  templateUrl: './pronunciation-train-front.html',
  styleUrl: './pronunciation-train-front.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PronunciationTrainFront implements OnDestroy {
  private readonly flashcardService = inject(FlashcardService);
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;
  private readonly chunks: Float32Array[] = [];
  private recordingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private assessmentSubscription: Subscription | null = null;
  private activeAssessmentFlashcardId: number | null = null;
  private currentFlashcardId: number | null = null;
  private sampleRate = 16000;

  readonly card = input.required<TrainFlashcard>();
  readonly canSpeakQuestion = input(false);
  readonly isQuestionAudioPlaying = input(false);

  readonly speak = output<void>();
  readonly assessmentComplete = output<void>();

  readonly isRecording = signal(false);
  readonly isAssessing = signal(false);
  readonly errorMessage = signal('');
  readonly assessment = signal<FlashcardPronunciationAssessmentResponse | null>(
    null,
  );
  readonly selectedWordIndex = signal<number | null>(null);
  readonly t = this.deckTranslations.t;

  private readonly resetOnCardChange = effect(() => {
    const cardId = this.card().id;
    if (this.currentFlashcardId === null) {
      this.currentFlashcardId = cardId;
      return;
    }

    if (this.currentFlashcardId !== cardId) {
      this.currentFlashcardId = cardId;
      this.resetForCardChange();
    }
  });

  readonly pronunciationTokens = computed(() =>
    this.createPronunciationTokens(
      this.card().frontText || this.t()['train.noQuestionText'],
      this.assessment()?.words ?? [],
    ),
  );

  readonly selectedToken = computed(() => {
    const selectedWordIndex = this.selectedWordIndex();
    if (selectedWordIndex == null) {
      return null;
    }

    return (
      this.pronunciationTokens().find(
        (token) => token.index === selectedWordIndex && token.word !== null,
      ) ?? null
    );
  });

  ngOnDestroy(): void {
    this.stopRecording(false);
    this.cancelAssessmentRequest();
  }

  async toggleRecording(): Promise<void> {
    if (this.isAssessing()) {
      return;
    }

    if (this.isRecording()) {
      this.stopRecording(true);
      return;
    }

    await this.startRecording();
  }

  selectWord(index: number): void {
    const token = this.pronunciationTokens().find(
      (item) => item.index === index,
    );
    if (!token?.word) {
      return;
    }

    this.selectedWordIndex.update((current) =>
      current === index ? null : index,
    );
  }

  wordToneClass(word: FlashcardPronunciationAssessmentWord | null): string {
    if (!word) {
      return 'pronunciation-word-neutral';
    }

    const errorType = word.errorType?.toLowerCase();
    if (errorType && errorType !== 'none' && errorType !== 'unknown') {
      return 'pronunciation-word-alert';
    }

    if (word.score == null) {
      return 'pronunciation-word-neutral';
    }

    if (word.score >= 85) {
      return 'pronunciation-word-good';
    }

    if (word.score >= 70) {
      return 'pronunciation-word-warn';
    }

    return 'pronunciation-word-alert';
  }

  phonemeStatus(phoneme: FlashcardPronunciationAssessmentPhoneme): string {
    if (phoneme.score == null) {
      return 'Missed';
    }

    if (phoneme.score >= 85) {
      return 'Good';
    }

    return 'Needs practice';
  }

  phonemeStatusClass(phoneme: FlashcardPronunciationAssessmentPhoneme): string {
    if (phoneme.score == null) {
      return 'pronunciation-phoneme-missed';
    }

    if (phoneme.score >= 85) {
      return 'pronunciation-phoneme-good';
    }

    return 'pronunciation-phoneme-practice';
  }

  wordDetailEmptyMessage(word: FlashcardPronunciationAssessmentWord): string {
    if (this.wordErrorType(word) === 'omission') {
      return this.t()['train.pronunciationWordOmitted'];
    }

    return this.t()['train.pronunciationNoPhonemeDetail'];
  }

  private wordErrorType(
    word: FlashcardPronunciationAssessmentWord,
  ): string | null {
    return word.errorType?.trim().toLocaleLowerCase() || null;
  }

  private async startRecording(): Promise<void> {
    this.errorMessage.set('');
    this.assessment.set(null);
    this.selectedWordIndex.set(null);
    this.chunks.length = 0;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioContext = new AudioContext();
      this.sampleRate = this.audioContext.sampleRate;
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
      this.processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        this.chunks.push(new Float32Array(input));
      };
      this.source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
      this.isRecording.set(true);
      this.recordingTimeoutId = setTimeout(
        () => this.stopRecording(true),
        30000,
      );
    } catch {
      this.errorMessage.set(this.t()['train.pronunciationMicUnavailable']);
      this.cleanupAudio();
    }
  }

  private stopRecording(shouldAssess: boolean): void {
    if (!this.isRecording() && !this.stream) {
      return;
    }

    this.isRecording.set(false);
    this.clearRecordingTimeout();
    this.cleanupAudio();

    if (!shouldAssess || this.chunks.length === 0) {
      return;
    }

    const wavBlob = this.createWavBlob(this.mergeChunks(this.chunks));
    this.assess(wavBlob);
  }

  private assess(wavBlob: Blob): void {
    const flashcardId = this.card().id;
    this.cancelAssessmentRequest();
    this.activeAssessmentFlashcardId = flashcardId;
    this.isAssessing.set(true);
    this.assessmentSubscription = this.flashcardService
      .assessPronunciation(flashcardId, wavBlob)
      .pipe(
        finalize(() => {
          if (this.activeAssessmentFlashcardId === flashcardId) {
            this.activeAssessmentFlashcardId = null;
            this.assessmentSubscription = null;
            this.isAssessing.set(false);
          }
        }),
      )
      .subscribe({
        next: (assessment) => {
          if (this.card().id !== flashcardId) {
            return;
          }

          this.assessment.set(assessment);
          this.selectedWordIndex.set(null);
          this.assessmentComplete.emit();
        },
        error: (error: Error) => {
          if (this.card().id !== flashcardId) {
            return;
          }

          this.errorMessage.set(
            error?.message ?? this.t()['train.pronunciationAssessError'],
          );
        },
      });
  }

  private resetForCardChange(): void {
    this.stopRecording(false);
    this.cancelAssessmentRequest();
    this.errorMessage.set('');
    this.assessment.set(null);
    this.selectedWordIndex.set(null);
    this.chunks.length = 0;
    this.isRecording.set(false);
    this.isAssessing.set(false);
  }

  private cancelAssessmentRequest(): void {
    const subscription = this.assessmentSubscription;
    this.assessmentSubscription = null;
    this.activeAssessmentFlashcardId = null;
    subscription?.unsubscribe();
    this.isAssessing.set(false);
  }

  private cleanupAudio(): void {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    void this.audioContext?.close();
    this.processor = null;
    this.source = null;
    this.stream = null;
    this.audioContext = null;
  }

  private clearRecordingTimeout(): void {
    if (this.recordingTimeoutId) {
      clearTimeout(this.recordingTimeoutId);
      this.recordingTimeoutId = null;
    }
  }

  private mergeChunks(chunks: Float32Array[]): Float32Array {
    const length = chunks.reduce((total, chunk) => total + chunk.length, 0);
    const result = new Float32Array(length);
    let offset = 0;
    chunks.forEach((chunk) => {
      result.set(chunk, offset);
      offset += chunk.length;
    });
    return result;
  }

  private createWavBlob(samples: Float32Array): Blob {
    const bytesPerSample = 2;
    const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
    const view = new DataView(buffer);
    this.writeAscii(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * bytesPerSample, true);
    this.writeAscii(view, 8, 'WAVE');
    this.writeAscii(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, this.sampleRate, true);
    view.setUint32(28, this.sampleRate * bytesPerSample, true);
    view.setUint16(32, bytesPerSample, true);
    view.setUint16(34, 16, true);
    this.writeAscii(view, 36, 'data');
    view.setUint32(40, samples.length * bytesPerSample, true);

    let offset = 44;
    samples.forEach((sample) => {
      const clamped = Math.max(-1, Math.min(1, sample));
      view.setInt16(
        offset,
        clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff,
        true,
      );
      offset += bytesPerSample;
    });

    return new Blob([buffer], { type: 'audio/wav' });
  }

  private writeAscii(view: DataView, offset: number, value: string): void {
    for (let i = 0; i < value.length; i++) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  }

  private createPronunciationTokens(
    text: string,
    words: FlashcardPronunciationAssessmentWord[],
  ): PronunciationTextToken[] {
    const parts = text.split(/(\s+)/);
    let wordIndex = 0;

    return parts
      .filter((part) => part.length > 0)
      .map((part, index) => {
        if (/^\s+$/.test(part)) {
          return {
            index,
            text: part,
            kind: 'space',
            word: null,
          };
        }

        const normalizedPart = this.normalizeAssessmentWord(part);
        const currentWord = words[wordIndex] ?? null;
        const normalizedWord = currentWord
          ? this.normalizeAssessmentWord(currentWord.text)
          : '';
        const word =
          currentWord && normalizedPart === normalizedWord
            ? currentWord
            : this.findUnmatchedAssessmentWord(words, normalizedPart);

        if (word) {
          wordIndex = Math.max(words.indexOf(word) + 1, wordIndex + 1);
        }

        return {
          index,
          text: part,
          kind: 'word',
          word,
        };
      });
  }

  private findUnmatchedAssessmentWord(
    words: FlashcardPronunciationAssessmentWord[],
    normalizedText: string,
  ): FlashcardPronunciationAssessmentWord | null {
    if (!normalizedText) {
      return null;
    }

    return (
      words.find(
        (word) => this.normalizeAssessmentWord(word.text) === normalizedText,
      ) ?? null
    );
  }

  private normalizeAssessmentWord(value: string): string {
    return value
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}']/gu, '')
      .trim();
  }
}
